import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Drawer, IconButton } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { PropsWithChildren, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import useWindowSize from '../../../../hooks/windowSize';
import { Language, languages } from '../../../../models/entities/language';
import { PagesRoutes } from '../../../../routing/PagesRoutes';
import { headerSelectors } from '../../../../state/ducks/header/selectors';
import { localizationSelectors } from '../../../../state/ducks/localization/selectors';
import { RootState } from '../../../../state/store/store';
import { styled } from '../../../shared/Theme/theme';
import SideBarItem, { availableIcons } from './SideBarItem';

import clsx from 'clsx';
import { featureFlagsModel } from '../../../../models/entities/businessPartner/settings';
import claimsType from '../../../../models/entities/claimsType';
import { userSelectors } from '../../../../state/ducks/user/selectors';
import { platformSuperAdminSelectors } from '../../../../state/ducks/platformSuperAdmin/selectors';
import { claimsDomainsType } from '../../../../userClaims';

export type linkItem = {
    icon: availableIcons;
    route: string;
    text: string;
    iconSize: number;
    subItems?: Array<linkItem>;
    onClick?: () => void;
};
type Props = object & {
    isForwarder: boolean;
    onLanguageSelected: (language: Language) => void;
    localizations: any;
    isOpen: boolean;
    setSideBarOpen: (open: boolean) => void;
    getUserClaims: (domain: claimsDomainsType) => claimsType;
    featureFlags?: featureFlagsModel;
    printMode?: boolean;
    isPlatformSuperAdmin?: boolean;
};

export const isItemSelected = (currentURL: string, itemRoute: string, subItems?: Array<linkItem>) => {
    if (currentURL === '/' && itemRoute === '/') return true;
    if (itemRoute === '/' && currentURL !== '/') return false;

    let oneOfSubItemsIsSelected = false;
    if (subItems) {
        oneOfSubItemsIsSelected = subItems.some((sub) => isItemSelected(currentURL, sub.route, sub.subItems));
    }
    return currentURL.indexOf(itemRoute) !== -1 || oneOfSubItemsIsSelected;
};

const drawerWidth = 240;

// Styled components moved outside component to prevent recreation on each render
const StyledDrawer = muiStyled(Drawer, {
    shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen: boolean }>(({ theme, isOpen }) => ({
    width: isOpen ? drawerWidth : 60,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    '& .MuiDrawer-paper': {
        paddingTop: '64px',
        zIndex: 0,
        border: 'none',
        overflowX: 'hidden',
        width: isOpen ? drawerWidth : 60,
        transition: theme.transitions.create(['width', 'background-image'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
        }),
        background: '#252b4a'
    },
    [theme.breakpoints.down(769)]: {
        display: 'none',
        width: 0
    }
}));

const StyledMain = muiStyled('main', {
    shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'printMode'
})<{ isOpen: boolean; printMode?: boolean }>(({ theme, isOpen, printMode }) => ({
    flexGrow: 1,
    position: 'relative',
    height: '100%',
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter
    }),
    marginLeft: isOpen && !printMode ? drawerWidth : 60,
    [theme.breakpoints.down(769)]: {
        marginLeft: 0
    }
}));

const BottomSection = styled('div')({
    bottom: 0,
    position: 'absolute',
    width: '100%',
    paddingRight: '4px'
});

const ArrowIconWrapper = styled('div')({
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end'
});

const SideBar = ({
    isForwarder,
    localizations,
    isOpen,
    featureFlags,
    printMode,
    isPlatformSuperAdmin,
    onLanguageSelected,
    setSideBarOpen,
    getUserClaims,
    children
}: PropsWithChildren<Props>) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [windowWidth] = useWindowSize();
    useEffect(() => {
        if (windowWidth > 1350 && localStorage.getItem('SIDEBAR_OPEN') !== 'false') setSideBarOpen(true);
        else if (windowWidth <= 1350) setSideBarOpen(false);
    }, [windowWidth]);
    const handleDrawerOpen = () => {
        setSideBarOpen(true);
        localStorage.setItem('SIDEBAR_OPEN', 'true');
    };

    const handleDrawerClose = () => {
        setSideBarOpen(false);
        localStorage.setItem('SIDEBAR_OPEN', 'false');
    };

    const buildItem = (icon: availableIcons, itemRoute: string, text: string, iconSize: number, subItems?: Array<linkItem>): linkItem => {
        const linkItem: linkItem = {
            icon,
            route: itemRoute,
            text,
            iconSize,
            subItems,
            onClick: () => navigate(itemRoute)
        };

        return linkItem;
    };

    const sideBarDefault: Array<linkItem> = [buildItem('dashboard', PagesRoutes.Home, localizations.links['dashboard'], 1)];

    if (!featureFlags?.UNDER_FORWARDER && isForwarder) {
        sideBarDefault.push(buildItem('grid', PagesRoutes.AccountingOversight, 'Accounting Oversight', 1));
    }

    if ((featureFlags?.CHAT && windowWidth < 768) || featureFlags?.CHAT_PAGE)
        sideBarDefault.push(buildItem('messages', PagesRoutes.Messages, localizations.links['messages'], 1));

    // features
    if (featureFlags?.CATALOG && !featureFlags?.UNDER_FORWARDER) {
        const catalogItems: linkItem[] = [buildItem('products', PagesRoutes.Products, localizations.links['products'], 1.2)];

        if (featureFlags?.INVENTORY) {
            catalogItems.push(buildItem('inventory', PagesRoutes.Inventory, localizations.links['inventory'], 1.2));
        }

        if (featureFlags?.INTERNAL_FLOW) {
            catalogItems.push(buildItem('inventory', PagesRoutes.InternalFlowInventoryLogs, localizations.links['inventory'], 1.2));
        }
        sideBarDefault.push(buildItem('catalog', PagesRoutes.Products, localizations.links['catalog'], 1, catalogItems));
    }

    if ((featureFlags?.PO || featureFlags?.PD) && !featureFlags?.UNDER_FORWARDER) {
        const arr: linkItem[] = [];
        if (featureFlags?.PD && getUserClaims('pd').view_pd)
            arr.push(buildItem('purchaseDemands', PagesRoutes.PurchaseDemands, localizations.links['purchaseDemands'], 1.2));
        if (featureFlags?.PO) arr.push(buildItem('purchaseOrders', PagesRoutes.PurchaseOrders, localizations.links['purchaseOrders'], 1.2));
        sideBarDefault.push(
            buildItem(
                'procurement',
                featureFlags?.PD && getUserClaims('pd').view_pd ? PagesRoutes.PurchaseDemands : PagesRoutes.PurchaseOrders,
                localizations.links['procurement'],
                1,
                arr.length > 0 ? arr : undefined
            )
        );
    }

    sideBarDefault.push(
        buildItem('shipments', PagesRoutes.Shipments, localizations.links['shipments'], 1, [
            buildItem('grid', PagesRoutes.Shipments, 'Grid', 1.2),
            buildItem('board', PagesRoutes.ShipmentsBoard, 'Board', 1.2),
            buildItem('grid', PagesRoutes.ShipmentsConsolidations, 'Consolidations', 1.2)
        ])
    );
    sideBarDefault.push(buildItem('orders', PagesRoutes.Followup, localizations.links['followUps'], 1));

    if (featureFlags?.CONTAINERS && !featureFlags?.UNDER_FORWARDER)
        sideBarDefault.push(buildItem('containers', PagesRoutes.Containers, localizations.links['containers'], 1));

    if (featureFlags?.ORDERS && !featureFlags?.UNDER_FORWARDER) {
        sideBarDefault.push(
            buildItem('orders', PagesRoutes.Orders, localizations.links['orders'], 1, [
                buildItem('grid', PagesRoutes.Orders, 'Grid', 1.2),
                buildItem('board', PagesRoutes.OrdersBoard, 'Board', 1.2)
            ])
        );
    }

    if (featureFlags?.TARIFF && isForwarder) sideBarDefault.push(buildItem('tariffs', PagesRoutes.Tariffs, localizations.links['tariffs'], 1));

    if (!featureFlags?.UNDER_FORWARDER)
        sideBarDefault.push(buildItem('businessPartners', PagesRoutes.BusinessPartners, localizations.links['companies'], 1));

    if (getUserClaims('admin').view && !featureFlags?.UNDER_FORWARDER) {
        const adminSubItems = [
            buildItem('users', PagesRoutes.UsersMgmt, localizations.links.users_mgmt, 1.2),
            buildItem('companies', PagesRoutes.CompaniesMgmt, localizations.links.companies_mgmt, 1.2),
            buildItem('partners_relations', PagesRoutes.CompaniesRelations, localizations.links.partners_relations, 1.2),
            // Carrier Weight Pricing hidden (decommission phase 1) — gated behind CARRIER_WEIGHT_PRICING flag.
            ...(featureFlags?.CARRIER_WEIGHT_PRICING
                ? [buildItem('partners_relations', PagesRoutes.CarrierWeightPricing, localizations.links.carrier_weight_pricing, 1.2)]
                : []),
            buildItem('deleted_records', PagesRoutes.DeletedRecords, localizations.links.deleted_records, 1)
        ];

        // Add audit trail for users with admin management claims
        if (getUserClaims('admin').view) {
            adminSubItems.push(
                buildItem('audit_trail', PagesRoutes.AuditTrail, localizations.links.audit_trail || 'Audit Trail', 1)
            );
        }

        sideBarDefault.push(buildItem('admin', PagesRoutes.UsersMgmt, localizations.links.admin, 1, adminSubItems));
    } else {
        sideBarDefault.push(buildItem('users', PagesRoutes.UsersMgmt, localizations.links.users_mgmt, 1));
    }

    if (isPlatformSuperAdmin) {
        sideBarDefault.push(buildItem('platform', PagesRoutes.PlatformHub, localizations.links.platform || 'Platform', 1));
    }

    const handleClick = (route: string) => {
        navigate(route);
    };
    return (
        <div style={{ height: '100%' }}>
            {!printMode && (
                <MobileBarWrapper>
                    {sideBarDefault.map((item) => (
                        <SideBarItem
                            key={item.route}
                            onClick={() => handleClick(item.route)}
                            icon={item.icon}
                            isSelected={isItemSelected(location.pathname, item.route)}
                            text={item.text}
                            iconSize={1}
                        />
                    ))}
                </MobileBarWrapper>
            )}
            {!printMode && (
                <StyledDrawer
                    variant="permanent"
                    isOpen={isOpen}
                >
                    <div>
                        {sideBarDefault.map((item) => (
                            <SideBarItem
                                key={item.route}
                                onClick={() => handleClick(item.route)}
                                icon={item.icon}
                                isSelected={isItemSelected(location.pathname, item.route, item.subItems)}
                                text={item.text}
                                subItems={item.subItems}
                                iconSize={item.iconSize}
                            />
                        ))}
                    </div>
                    <BottomSection>
                        <LanguageSelector>
                            <span onClick={() => onLanguageSelected(languages.English)}>EN</span> |{' '}
                            <span onClick={() => onLanguageSelected(languages.Spanish)}>ES</span> |{' '}
                            <span onClick={() => onLanguageSelected(languages.Chinese)}>CN</span>
                        </LanguageSelector>
                        <ArrowIconWrapper>
                            <IconButton onClick={isOpen ? handleDrawerClose : handleDrawerOpen} style={{ width: '54px', color: '#fff', right: 0 }}>
                                {isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                            </IconButton>
                        </ArrowIconWrapper>
                    </BottomSection>
                </StyledDrawer>
            )}
            <StyledMain isOpen={isOpen} printMode={printMode}>
                {children}
            </StyledMain>
        </div>
    );
};

const MobileBarWrapper = styled.div`
    display: none;
    @media screen and (max-width: 768px) {
        display: initial;
        height: 40px;
        display: flex;
        width: 100%;
        text-align: left;
        box-sizing: border-box;
        bottom: 0px;
        position: fixed;
        background-color: #252b4a;
        z-index: 1000;
        justify-content: space-around;

        & div {
            display: flex;
            height: 40px;
            justify-content: center;
            cursor: pointer;
            color: #fff;
            box-sizing: border-box;
        }
    }

    @media screen and (max-height: 400px) {
        display: none;
    }
`;

const LanguageSelector = styled.div`
    font-size: ${(props) => props.theme.fontSizes.languageSelector};
    color: ${(props) => props.theme.colors.sideBarItem};
    font-weight: bold;
    text-align: center;
    cursor: pointer;

    @media screen and (max-width: 768px) {
        display: none;
    }
`;

const mapStateToProps = (state: RootState) => ({
    localizations: localizationSelectors.sidebar(state),
    isOpen: headerSelectors.sideBarOpen(state),
    featureFlags: userSelectors.featureFlags(state),
    isForwarder: userSelectors.userInfo(state).companyType === 'FORWARDER',
    isPlatformSuperAdmin: platformSuperAdminSelectors.isPlatformSuperAdmin(state),
    getUserClaims: (domain: claimsDomainsType) => userSelectors.getUserClaims(state)(domain)
});

const mapDispatchToProps = (dispatch: any) => ({
    onLanguageSelected: (language: Language) => dispatch.localization.changeLanguage(language),
    setSideBarOpen: (open: boolean) => dispatch.header.setSideBarOpen(open)
});

export default connect(mapStateToProps, mapDispatchToProps)(SideBar);
